import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DocumentEditService } from '../../document-edit.service';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { Z } from '@angular/cdk/keycodes';
import { FiReplaceLinkTypes } from '@fi-shared/fi-directives/replace-with-links/replace-with-links.directive';
import { ObjectTypes } from '@fi-shared/types';
import { TextEditorInsertMediaService } from '@fi-shared/text-editor/text-editor-insert-media.service';
import { FiDndOverlayComponent } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-overlay.component';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { findClosest, isDefined } from '@fi-shared/utils';
import { FiAIAssistanceHelperService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance-helper.service';
import {
    FI_AI_ASSISTANCE_EDITOR_ID,
    getExistingAiRequestEditorElement,
    getExistingRequestInputEl,
} from '@fi-shared/fi-ai-assistance/fi-ai-assistance-request-container';
import { isNull } from 'util';
import { FiTextPanelType } from '@fi-shared/fi-text-panel/fi-text-panel-type';
import { FiTextPanelHelperService } from '@fi-shared/fi-text-panel/text-panel-services/fi-text-panel-helper.service';
import { SimpleTextPanelComponent } from '@fi-shared/fi-text-panel/simple-text-panel/simple-text-panel.component';
import { AdvancedTextPanelComponent } from '@fi-shared/fi-text-panel/advanced-text-panel/advanced-text-panel.component';
import { TextPanelBase } from '@fi-shared/fi-text-panel/simple-text-panel/text-panel-base';

@Component({
    selector: 'fi-document-layout-item-text-edit',
    template: `
        @if (textPanelType === FiTextPanelType.Simple) {
            <fi-simple-text-panel
                [hidden]="!(isTextPanelVisible$ | async)"
                [componentElement]="editable"
                [objectType]="ObjectTypes.Document"
            />
        }
        @if (textPanelType === FiTextPanelType.Advanced) {
            <fi-advanced-text-panel [hidden]="!(isTextPanelVisible$ | async)" [objectType]="ObjectTypes.Document" />
        }
        <fi-text-item-table-panel-handler [editableElement]="editable" />
        <fi-text-item-media-panel-handler
            [editableElement]="editable"
            (setCaretVisibility)="setCaretVisibility($event)"
        />
        <fi-ai-assistance-panel-handler [editableElement]="editable" />
        <fi-dnd-overlay #fiDndOverlayComponent [parentElementRef]="elementRef" (uploadFiles)="uploadFiles($event)" />
        <div
            contenteditable="true"
            #editable
            [(fiContenteditableModel)]="ngModel"
            (afterFirstRender)="onEditorInit()"
            [mediaBankFileReferences]="mediaBankFileReferences"
            (fiContenteditableModelChange)="textModelChange($event)"
            class="text-editor branded-color-caret"
            [ngClass]="{ 'transparent-caret': !isCaretVisible, expanded: (isDraggingFile() | async) }"
            [attr.placeholder]="'agreement.text_block_hint' | fiAppText"
            [attr.dir]="getDirRtlAttr()"
            [style.text-align]="getTextAlignRtlAttr()"
            [fiReplaceWithLinks]="replaceLinkTypes"
            (focus)="onEditorFocus()"
            (click)="setFocusedElement()"
        ></div>
    `,
    styleUrls: ['./layout-item-text-edit.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiDocumentLayoutItemTextEditComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-document-layout-item-text-edit' },
    standalone: false,
})
export class FiDocumentLayoutItemTextEditComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() ngModel: string;
    @Output('ngModelChange') ngModelChange = new EventEmitter<string>();
    @Input({ required: true }) textPanelType: FiTextPanelType;
    @Input() replaceLinkTypes = [FiReplaceLinkTypes.Url, FiReplaceLinkTypes.Email];
    @Input('placeholder') textPlaceholder: string = '';
    @Input() mediaBankFileReferences: IMediaBankFileReference[];
    @Input() skipRemoveNonExistingMediaReferences = false;
    @Input() contentLanguageId: FiApplicationLanguageType;
    @ViewChild('editable', { static: true }) editableElement: ElementRef<HTMLElement>;
    @ViewChild('fiDndOverlayComponent') fiDndOverlayComponent: FiDndOverlayComponent;
    @Output('fiAfterInit') fiAfterInit: EventEmitter<any> = new EventEmitter();
    @Output() editorFocus = new EventEmitter();

    @ViewChild(SimpleTextPanelComponent) simpleTextPanelComponent: SimpleTextPanelComponent;
    @ViewChild(AdvancedTextPanelComponent) advancedTextPanelComponent: AdvancedTextPanelComponent;

    isTextPanelVisible$ = new BehaviorSubject(false);
    isCaretVisible: boolean = true;
    focusedNodeElement: HTMLElement;
    private saveStateTimeout;
    private initialText: string;
    FiReplaceLinkTypes = FiReplaceLinkTypes;

    constructor(
        private documentEditService: DocumentEditService,
        private textItemHelperService: FiTextItemHelperService,
        private fileUploadService: FiFileUpload2Service,
        public elementRef: ElementRef<HTMLElement>,
        private textEditorService: TextEditorService,
        private textEditorHistoryService: TextEditorHistoryService,
        private textEditorInsertMediaService: TextEditorInsertMediaService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiAIAssistanceHelperService: FiAIAssistanceHelperService,
        private fiTextPanelHelperService: FiTextPanelHelperService,
    ) {}

    ngOnInit() {
        if (!this.editableElement.nativeElement.hasAttribute('id')) {
            this.editableElement.nativeElement.setAttribute(
                'id',
                this.textEditorHistoryService.getContentEditableElementUid(),
            );
        }
    }

    isDraggingFile(): Observable<boolean> {
        return this.fiDndOverlayComponent?.fiDndHelper.dragging$;
    }

    uploadFiles(files: File[]): void {
        const selection = document.getSelection();
        const range = this.textEditorInsertMediaService.getRangeForMediaInsert(
            this.editableElement.nativeElement,
            selection,
        );

        this.fileUploadService.silentUploadFiles(files).then((mediaBankReferences: IMediaBankFileReference[]) => {
            range.deleteContents();
            this.textEditorInsertMediaService.createNodesFromResult(mediaBankReferences).then((nodes) => {
                this.textEditorInsertMediaService.insertMediaIntoTextEditorRange(range, selection, nodes);
            });
        });
    }

    ngAfterViewInit(): void {
        this.textItemHelperService.updateIFrameSourcesAndStyles(this.elementRef.nativeElement);
        // https://chainformation.atlassian.net/browse/FI-3705
        this.fileUploadService.vimeoFilesInProcessChanged$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.textItemHelperService.updateIFrameSourcesAndStyles(this.elementRef.nativeElement));

        fromEvent(this.elementRef.nativeElement, 'keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => {
                this.textEditorService.handleTabKeyPress($event);

                if (($event.ctrlKey || $event.metaKey) && $event.keyCode === Z) {
                    $event.preventDefault();
                    const undoResult = $event.shiftKey
                        ? this.textEditorHistoryService.redo()
                        : this.textEditorHistoryService.undo();
                    if (!undoResult) this.writeValue('');
                }
            });

        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((event: MouseEvent) => {
                const fiAiAssistanceEditor: HTMLElement = getExistingAiRequestEditorElement();
                if (
                    isDefined(fiAiAssistanceEditor) &&
                    !findClosest(event.target as HTMLElement, `#${FI_AI_ASSISTANCE_EDITOR_ID}`)
                ) {
                    if (!isNull(fiAiAssistanceEditor)) {
                        this.fiAIAssistanceHelperService.hideAiAssistanceEditor(fiAiAssistanceEditor);
                    }
                }
            });

        const editableWrapperElement = this.editableElement.nativeElement.parentElement.parentElement; // div class="column not-empty-column"
        this.fiTextPanelHelperService.subscribeToTextPanelVisibilityChangeOnClick(
            editableWrapperElement,
            this.ngOnDestroy$,
            (isPanelVisible) => this.isTextPanelVisible$.next(isPanelVisible),
        );

        if (!this.skipRemoveNonExistingMediaReferences) {
            this.textItemHelperService.removeNonExistingMediaReferences(
                this.elementRef.nativeElement,
                this.mediaBankFileReferences,
            );
        }

        if (this.textPanelType !== FiTextPanelType.Simple) {
            this.fiTextPanelHelperService.subscribeToScrollUpdating(
                editableWrapperElement,
                this.getTextPanelComponent().elementRef.nativeElement,
                0,
                this.ngOnDestroy$,
            );
        }
    }

    private getTextPanelComponent(): TextPanelBase {
        if (this.textPanelType === FiTextPanelType.Simple) {
            return this.simpleTextPanelComponent;
        }
        if (this.textPanelType === FiTextPanelType.Advanced) {
            return this.advancedTextPanelComponent;
        }
        throw new Error(`getTextPanelComponent: unknown textPanelType ${this.textPanelType}`);
    }

    private isDestroyed = false;
    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.isDestroyed = true;
        this.clearSaveStateTimeout();
        this.fiAIAssistanceHelperService.tempAiRequestText = undefined;
    }

    private clearSaveStateTimeout() {
        if (this.saveStateTimeout) {
            this.saveStateTimeout = undefined;
            clearTimeout(this.saveStateTimeout);
        }
    }

    onEditorInit() {
        this.fiAfterInit.emit(this.editableElement.nativeElement);
    }

    // note: not used ?
    propagateChange = (_: any) => {};

    writeValue(obj: any) {
        this.ngModel = obj;
    }

    registerOnChange(fn: any) {
        this.propagateChange = fn;
    }

    // note: not used ?
    registerOnTouched(fn: any) {}

    textModelChange($event: string) {
        this.clearSaveStateTimeout();

        if (this.initialText === null) this.initialText = $event;

        this.ngModelChange.emit($event);
        this.setFocusedElement();
        this.saveStateTimeout = setTimeout(() => {
            if (this.isDestroyed) return;
            this.checkSaveHistory($event);
        }, 500);
    }

    private checkSaveHistory(currentHTML) {
        if (this.initialText !== currentHTML) {
            this.textEditorHistoryService.saveState(this.initialText, this.editableElement.nativeElement.id);
            this.initialText = currentHTML;
        }
    }

    @HostListener('mousedown')
    componentMouseDown() {
        this.documentEditService.MouseDownOnText = true;
    }

    @HostListener('document:click')
    documentMouseUp() {
        this.documentEditService.MouseDownOnText = false;
    }

    onEditorFocus() {
        this.editorFocus.emit();
        this.setFocusedElement();
    }

    setFocusedElement() {
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
    }

    focusEditor() {
        this.editableElement.nativeElement.focus();
    }

    setCaretVisibility($event: boolean) {
        this.isCaretVisible = $event;
    }

    getDirRtlAttr() {
        return this.textItemHelperService.getDirRtlAttr(this.contentLanguageId);
    }

    getTextAlignRtlAttr() {
        return this.textItemHelperService.getTextAlignRtlAttr(this.contentLanguageId);
    }

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly FiTextPanelType = FiTextPanelType;
}
